import React from 'react';
import { useConfiguration } from '../../context/configurationContext';
import { categorySlug, findCategoryBySlug } from '../../util/categoryLinks';
import { NamedLink } from '../../components';
import { POPULAR_TIRE_SIZES, POPULAR_RIM_SIZES } from '../../util/dimensionRoutes';
import { openConsentSettings } from '../../util/tracking';
import wheelplaceLogo from '../../assets/wheelplace-logo.svg';
import css from './WheelplaceFooter.module.css';

/* Brand shortlist for the SearchPageMake route (/annonser/:make). These are
 * category ids, and the label comes from the category configuration — so a brand
 * that is renamed or removed in the Console cannot leave a dead link behind.
 *
 * It used to be a list of display labels turned into slugs with
 * label.toLowerCase().replace(/\s+/g, ''). SearchPage drops a category it does
 * not recognise, so a wrong slug answered 200 and showed *every* listing under
 * the brand's heading, indexable and wrong.
 *
 * Mercedes is `mercedes-benz` on Live (P25009-488). This list said `mercedes`,
 * Test's old id, so Mercedes was missing from the Live footer; Test now uses
 * Live's id too. An id the config does not have is dropped, never linked. */
const FOOTER_BRAND_IDS = [
  'audi', 'bmw', 'byd', 'citroen', 'cupra', 'dacia', 'fiat', 'ford', 'honda', 'hyundai',
  'kia', 'landrover', 'mazda', 'mercedes-benz', 'mg', 'nissan', 'peugeot', 'polestar',
  'porsche', 'renault', 'saab', 'seat', 'skoda', 'subaru', 'tesla', 'volkswagen', 'volvo',
];

const brandLinksFromConfig = categories =>
  FOOTER_BRAND_IDS.map(id => findCategoryBySlug(categories, id))
    .filter(Boolean)
    .map(category => ({ label: category.name, slug: categorySlug(category) }));

/* Size lists — clean indexable pages (/dack/:tireSize, /falgar/:rimSize). Shared
 * with the all-brands hub page so the two cannot drift apart. */
const dimensionLinks = POPULAR_TIRE_SIZES;
const rimLinks = POPULAR_RIM_SIZES;

/* Build a `?key=value&...` search string from a params object. */
const toSearchString = params =>
  '?' +
  Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

/* Events Cookiebot fires once it is initialised and able to show its banner.
 * `CookiebotOnLoad` is the one that matters: the consent events only fire after
 * a visitor has chosen something, so a first-time visitor would otherwise never
 * see the link. */
const CONSENT_MANAGER_EVENTS = [
  'CookiebotOnLoad',
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
];

/* "Change your cookie choice" — the way back into the consent banner after the
 * first visit. P25009-376 BR-4/AC-4.
 *
 * Only rendered once the consent manager is really usable: a visitor who blocks
 * the script, and any host Cookiebot has not been told about (localhost is one),
 * should not see a control that cannot do anything. The script loads async, so
 * the first render usually happens before it exists — hence the re-check on
 * every event above. */
const ConsentSettingsLink = () => {
  const [isAvailable, setIsAvailable] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsAvailable(typeof window.Cookiebot?.renew === 'function');
    check();
    CONSENT_MANAGER_EVENTS.forEach(name => window.addEventListener(name, check, false));
    return () =>
      CONSENT_MANAGER_EVENTS.forEach(name => window.removeEventListener(name, check, false));
  }, []);

  if (!isAvailable) {
    return null;
  }

  return (
    <li>
      <button type="button" className={css.colLinkButton} onClick={openConsentSettings}>
        Cookie-inställningar
      </button>
    </li>
  );
};

/* Generic chip-list renderer. Each item is either:
 *   { label, slug }            — brand link (SearchPageMake route)
 *   { label, params }          — filter link (SearchPage + query params)
 */
const SeoList = ({ items, variant }) => (
  <div className={css.seoChips}>
    {items.map((item, i) => {
      let link;
      if (variant === 'brand') {
        link = (
          <NamedLink name="SearchPageMake" params={{ make: item.slug }} className={css.seoLink}>
            {item.label}
          </NamedLink>
        );
      } else if (variant === 'tireDimension') {
        link = (
          <NamedLink
            name="TireDimensionPage"
            params={{ tireSize: item.slug }}
            className={css.seoLink}
          >
            {item.label}
          </NamedLink>
        );
      } else if (variant === 'rimDimension') {
        link = (
          <NamedLink name="RimDimensionPage" params={{ rimSize: item.slug }} className={css.seoLink}>
            {item.label}
          </NamedLink>
        );
      } else {
        link = (
          <NamedLink
            name="SearchPage"
            to={{ search: toSearchString(item.params) }}
            className={css.seoLink}
          >
            {item.label}
          </NamedLink>
        );
      }
      return (
        <span key={item.label}>
          {link}
          {i < items.length - 1 && <span className={css.seoSep}>·</span>}
        </span>
      );
    })}
  </div>
);

const socials = [
  { label: 'Facebook', href: 'https://facebook.com', icon: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z' },
  { label: 'Instagram', href: 'https://instagram.com', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
  { label: 'YouTube', href: 'https://youtube.com', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
  { label: 'TikTok', href: 'https://tiktok.com', icon: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
];

const WheelplaceFooter = () => {
  const config = useConfiguration();
  const brandLinks = brandLinksFromConfig(config.categoryConfiguration?.categories);

  return (
  <footer className={css.footer}>
    <div className={css.container}>
      <div className={css.top}>
        <div className={css.brandBlock}>
          <NamedLink name="LandingPage" className={css.brandLogoLink}>
            <img src={wheelplaceLogo} alt="Wheelplace" className={css.brandLogo} />
          </NamedLink>
          <p className={css.brandBlurb}>
            Sveriges smartaste marknadsplats för hjul, däck och fälgar. Köp och sälj tryggt — vi
            hjälper dig hitta rätt.
          </p>
          <div className={css.socials}>
            {socials.map(s => (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                className={css.socialLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className={css.socialIcon} viewBox="0 0 24 24">
                  <path d={s.icon} />
                </svg>
              </a>
            ))}
          </div>
        </div>

        <div className={css.navCols}>
          <div>
            <h4 className={css.colHeading}>Wheelplace</h4>
            <ul className={css.colList}>
              <li><NamedLink name="CMSPage" params={{ pageId: 'om-wheelplace' }} className={css.colLink}>Om Wheelplace</NamedLink></li>
              <li><NamedLink name="SearchPage" className={css.colLink}>Alla annonser</NamedLink></li>
              <li><NamedLink name="WheelplaceCreateListingPage" className={css.colLink}>Lägg in annons</NamedLink></li>
              <li><NamedLink name="SavedSearchesPage" className={css.colLink}>Bevakningar</NamedLink></li>
            </ul>
          </div>
          <div>
            <h4 className={css.colHeading}>Köpare &amp; säljare</h4>
            <ul className={css.colList}>
              <li><NamedLink name="SearchPage" className={css.colLink}>Privatpersoner</NamedLink></li>
              <li><NamedLink name="SearchPage" className={css.colLink}>Företag</NamedLink></li>
              {/* pub_adType is the key that actually separates tyres from rims.
                * These two used to pass pub_category=dack / =falgar, which is not
                * a configured filter — the API ignores it silently, so both links
                * returned every listing on the marketplace, tyres and rims mixed
                * (316 of 316 when this was checked). */}
              <li>
                <NamedLink name="SearchPage" to={{ search: '?pub_adType=tire' }} className={css.colLink}>
                  Begagnade däck
                </NamedLink>
              </li>
              <li>
                <NamedLink name="SearchPage" to={{ search: '?pub_adType=rim' }} className={css.colLink}>
                  Begagnade fälgar
                </NamedLink>
              </li>
            </ul>
          </div>
          <div>
            <h4 className={css.colHeading}>Juridiskt</h4>
            <ul className={css.colList}>
              <li><NamedLink name="TermsOfServicePage" className={css.colLink}>Allmänna villkor</NamedLink></li>
              <li><NamedLink name="PrivacyPolicyPage" className={css.colLink}>Integritetspolicy</NamedLink></li>
              <ConsentSettingsLink />
              <li><NamedLink name="CMSPage" params={{ pageId: 'kontakt' }} className={css.colLink}>Kontakt</NamedLink></li>
            </ul>
          </div>
        </div>
      </div>

      <hr className={css.divider} />

      <div className={css.seoGrid}>
        <div>
          <h4 className={css.colHeading}>Hjul per bilmärke</h4>
          <SeoList items={brandLinks} variant="brand" />
          {/* The list above is a curated shortlist; this reaches the rest, and
            * is the only link into the model pages. */}
          <NamedLink name="AllBrandsPage" className={css.seoLink}>
            Alla märken och modeller
          </NamedLink>
        </div>
        <div>
          <h4 className={css.colHeading}>Populära dimensioner</h4>
          <SeoList items={dimensionLinks} variant="tireDimension" />
        </div>
        <div>
          <h4 className={css.colHeading}>Fälgar</h4>
          <SeoList items={rimLinks} variant="rimDimension" />
        </div>
      </div>

      <hr className={css.bottomDivider} />
      <p className={css.copy}>
        &copy; {new Date().getFullYear()} Wheelplace AB. Alla rättigheter förbehållna.
      </p>
    </div>
  </footer>
  );
};

export default WheelplaceFooter;
